import React, { useCallback, useId, useMemo, useState } from "react"

import {
  useAnnotationsWithMetricOutputsTransport,
  useReviewMetricTransportInvalidation,
  useUpdateReviewAnnotationTransport,
} from "@/human-feedback/transport"
import { Checkbox, Skeleton, Switch } from "@coval/ui"

import { useActiveItemAutoScroll } from "@/hooks/useActiveItemAutoScroll"
import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import type { HumanReviewProject } from "@/types/api/HumanReviewProject"

import type { CriterionResult } from "../../types"
import { computeExperimentAgreement } from "../../utils/computeExperimentAgreement"
import {
  canRevealHumanVsAiAgreement,
  DEFAULT_SIMULATION_FILTERS,
  filterAndSortSimulations,
  formatDate,
  getAnnotationProjectIds,
  groupAnnotationsBySimulation,
  type SimulationFilters,
} from "../../utils/filterSimulations"
import { useTestDrawer, useTestMetricRuntime } from "../contexts"
import { useCanRunEvaluations } from "../hooks/useCanRunEvaluations"
import { MAX_TEST_CONVERSATIONS, resolveRunSelection } from "../runSelection"
import { extractCompositeResults } from "../utils/compositeResults"
import { hasRowConflict } from "../utils/hasRowConflict"
import { DisagreementLegend } from "./DisagreementLegend"
import { ExpectedBehaviorComparisonList } from "./ExpectedBehaviorComparisonList"
import {
  CRITERIA_VIEW_MODE,
  SimulationRow,
  type CriteriaViewMode,
  type ExtendedGroupedSimulationData,
} from "./SimulationReviewRow"

type Filters = SimulationFilters

interface SimulationReviewTableProps {
  experimentResults?: Record<
    string,
    {
      value: string | null
      rawFloatValue?: number | null
      criteriaResults?: CriterionResult[]
      explanation: string
      errorStatus?: string
      errorMessage?: string
    }
  >
  evaluatingIds?: Set<string>
  filters?: Filters
  isRunning?: boolean
  humanReviewProjects?: HumanReviewProject[]
}

export const SimulationReviewTable: React.FC<SimulationReviewTableProps> = ({
  experimentResults = {},
  evaluatingIds = new Set(),
  filters = DEFAULT_SIMULATION_FILTERS,
  isRunning = false,
  humanReviewProjects,
}) => {
  const {
    metric,
    includeOldVersions,
    setIncludeOldVersions,
    simulationIds: scopedSimulationIds,
    criteriaFirst,
    criteriaOverride,
    criteriaViewMode,
  } = useTestMetricRuntime()
  const versionToggleId = useId()
  const {
    handleRowClick,
    focusedSimulationIds,
    clearFocusedSimulationIds,
    selectedRunIds,
    setSelectedRunIds,
  } = useTestDrawer()
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null)
  const canRunEvaluations = useCanRunEvaluations()
  const isCompositeMetric =
    metric?.metric_manager === "manager_composite_evaluation"
  const effectiveCriteriaViewMode: CriteriaViewMode = isCompositeMetric
    ? criteriaViewMode
    : CRITERIA_VIEW_MODE.SCORE
  const relevantProjects = useMemo(
    () =>
      humanReviewProjects?.filter((project) =>
        metric?.id ? project.linked_metric_ids.includes(metric.id) : false,
      ) ?? [],
    [humanReviewProjects, metric?.id],
  )

  const invalidateReviewMetricTransport = useReviewMetricTransportInvalidation()

  const annotationProjectIds = useMemo(
    () => getAnnotationProjectIds(filters.project),
    [filters.project],
  )
  const {
    data: annotationsWithMetricOutputs,
    isLoading,
    isFetching,
    isError,
  } = useAnnotationsWithMetricOutputsTransport({
    metricId: metric?.id,
    projectIds: annotationProjectIds,
    simulationOutputIds: scopedSimulationIds,
    enabled:
      scopedSimulationIds === undefined || scopedSimulationIds.length > 0,
    includeMetricOutputDetails: isCompositeMetric,
  })
  const showHumanAgreementRate =
    isCompositeMetric &&
    canRevealHumanVsAiAgreement(filters.project, relevantProjects, isFetching)

  const updateAnnotationMutation = useUpdateReviewAnnotationTransport()

  const handleSetPrimary = useCallback(
    async (reviewId: string, allReviewIds: string[]) => {
      setSettingPrimaryId(reviewId)
      try {
        const otherIds = allReviewIds.filter((id) => id !== reviewId)
        await Promise.all(
          otherIds.map((otherId) =>
            updateAnnotationMutation.mutateAsync({
              annotationId: otherId,
              priority: "STANDARD",
            }),
          ),
        )

        await updateAnnotationMutation.mutateAsync({
          annotationId: reviewId,
          priority: "PRIMARY",
        })
        invalidateReviewMetricTransport()
      } catch {
        // Mutation errors are reported by the transport hook.
      } finally {
        setSettingPrimaryId(null)
      }
    },
    [updateAnnotationMutation, invalidateReviewMetricTransport],
  )

  const baseGroupedData = useMemo(() => {
    const grouped = groupAnnotationsBySimulation(
      annotationsWithMetricOutputs,
      metric?.id,
      metric?.output_type || "string",
      filters.includeUnreviewed,
    )
    if (!scopedSimulationIds) {
      return grouped
    }
    const scope = new Set(scopedSimulationIds)
    return grouped.filter((item) => scope.has(item.simulationOutputId))
  }, [
    annotationsWithMetricOutputs,
    metric?.id,
    metric?.output_type,
    filters.includeUnreviewed,
    scopedSimulationIds,
  ])

  const groupedData = useMemo((): ExtendedGroupedSimulationData[] => {
    return baseGroupedData.map((baseItem) => {
      const experimentResult = experimentResults[baseItem.simulationOutputId]
      const metricOutput =
        annotationsWithMetricOutputs?.metricOutputs[metric?.id || ""]?.[
          baseItem.simulationOutputId
        ]

      const aiReviewMetadata = formatDate(metricOutput?.created_at || null)
      const displayValue = metricOutput
        ? (metric?.getDisplayValueString(metricOutput) ?? null)
        : null

      const isStaleVersion =
        metricOutput && metric
          ? metric.isMetricOutputStale(metricOutput)
          : false
      const versionStatus =
        metricOutput && metric
          ? metric.getOutputVersionStatus(metricOutput)
          : null

      const aiCriteria = extractCompositeResults(metricOutput)

      return {
        ...baseItem,
        aiReview: {
          value: displayValue || null,
          explanation: metricOutput?.explanation || null,
          metadata: aiReviewMetadata,
        },
        aiCriteria,
        mostRecentExperiment: {
          value: experimentResult?.value || null,
          rawFloatValue: experimentResult?.rawFloatValue,
          criteriaResults: experimentResult?.criteriaResults,
          explanation: experimentResult?.explanation || null,
          errorStatus: experimentResult?.errorStatus,
          errorMessage: experimentResult?.errorMessage,
        },
        isStaleVersion,
        versionStatus,
      }
    })
  }, [baseGroupedData, experimentResults, annotationsWithMetricOutputs, metric])

  const filteredAndSortedData = useMemo(() => {
    const baseData = groupedData.map((item) => ({
      simulationOutputId: item.simulationOutputId,
      humanReviews: item.humanReviews,
      rawAnnotations: item.rawAnnotations,
      rawMetricOutput: item.rawMetricOutput,
    }))

    const filtered = filterAndSortSimulations(
      baseData,
      filters,
      metric,
      undefined,
      relevantProjects,
      effectiveCriteriaViewMode,
      annotationProjectIds !== undefined,
      isFetching,
    )

    const filteredMap = new Map(
      filtered.map((item, index) => [item.simulationOutputId, { item, index }]),
    )

    return groupedData
      .filter((item) => filteredMap.has(item.simulationOutputId))
      .map((item) => {
        const filteredEntry = filteredMap.get(item.simulationOutputId)!
        return {
          ...item,
          humanReviews: filteredEntry.item.humanReviews,
          rawAnnotations: filteredEntry.item.rawAnnotations,
          rawMetricOutput: filteredEntry.item.rawMetricOutput,
        }
      })
      .sort((a, b) => {
        const indexA = filteredMap.get(a.simulationOutputId)!.index
        const indexB = filteredMap.get(b.simulationOutputId)!.index
        return indexA - indexB
      })
  }, [
    groupedData,
    filters,
    metric,
    relevantProjects,
    effectiveCriteriaViewMode,
    annotationProjectIds,
    isFetching,
  ])

  const displayData = useMemo(() => {
    if (!focusedSimulationIds) {
      return filteredAndSortedData
    }
    const ids = new Set(focusedSimulationIds)
    return filteredAndSortedData.filter((d) => ids.has(d.simulationOutputId))
  }, [filteredAndSortedData, focusedSimulationIds])

  const conflictIds = useMemo(
    () =>
      new Set(
        displayData
          .filter(
            (data) =>
              (!data.isStaleVersion || includeOldVersions) &&
              hasRowConflict(data, metric, effectiveCriteriaViewMode),
          )
          .map((data) => data.simulationOutputId),
      ),
    [displayData, includeOldVersions, metric, effectiveCriteriaViewMode],
  )

  const orderedIds = useMemo(
    () => filteredAndSortedData.map((d) => d.simulationOutputId),
    [filteredAndSortedData],
  )
  const { runIdSet } = useMemo(
    () => resolveRunSelection(orderedIds, selectedRunIds),
    [orderedIds, selectedRunIds],
  )
  const atCapacity = runIdSet.size >= MAX_TEST_CONVERSATIONS

  const displayedIds = useMemo(
    () => displayData.map((d) => d.simulationOutputId),
    [displayData],
  )
  const selectedDisplayedCount = useMemo(
    () => displayedIds.filter((id) => runIdSet.has(id)).length,
    [displayedIds, runIdSet],
  )
  const headerCheckedState: boolean | "indeterminate" =
    displayedIds.length === 0 || selectedDisplayedCount === 0
      ? false
      : selectedDisplayedCount === displayedIds.length
        ? true
        : "indeterminate"

  const handleToggleRow = useCallback(
    (id: string) => {
      const next = new Set(selectedRunIds ?? runIdSet)
      if (next.has(id)) {
        next.delete(id)
      } else {
        if (next.size >= MAX_TEST_CONVERSATIONS) {
          return
        }
        next.add(id)
      }
      setSelectedRunIds(next)
    },
    [selectedRunIds, runIdSet, setSelectedRunIds],
  )

  const handleToggleSelectAll = useCallback(() => {
    const next = new Set(selectedRunIds ?? runIdSet)
    if (selectedDisplayedCount === displayedIds.length) {
      for (const id of displayedIds) {
        next.delete(id)
      }
    } else {
      for (const id of displayedIds) {
        if (next.size >= MAX_TEST_CONVERSATIONS) {
          break
        }
        next.add(id)
      }
    }
    setSelectedRunIds(next)
  }, [
    selectedRunIds,
    runIdSet,
    selectedDisplayedCount,
    displayedIds,
    setSelectedRunIds,
  ])

  const {
    handlePointerEnter,
    handlePointerLeave,
    isActiveItem,
    keyboardActiveItemId,
  } = useCollectionPageShortcuts({
    enabled: true,
    getItemId: (item) => item.simulationOutputId,
    itemLabel: "simulation",
    items: displayData,
    onOpenItem: (item) => handleRowClick(item.simulationOutputId),
  })

  useActiveItemAutoScroll({
    activeItemId: keyboardActiveItemId,
    selectorAttribute: "data-simulation-review-id",
  })

  const handleToggleExpand = (simulationOutputId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(simulationOutputId)) {
        newSet.delete(simulationOutputId)
      } else {
        newSet.add(simulationOutputId)
      }
      return newSet
    })
  }

  const validExperimentResults = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(experimentResults).filter(
          (entry): entry is [string, { value: string; explanation: string }] =>
            entry[1].value !== null,
        ),
      ),
    [experimentResults],
  )

  const experimentAgreementStats = useMemo(
    () =>
      metric
        ? computeExperimentAgreement(
            groupedData,
            validExperimentResults,
            metric,
            effectiveCriteriaViewMode,
          )
        : null,
    [validExperimentResults, groupedData, metric, effectiveCriteriaViewMode],
  )

  const toolbar = metric?.hasVersionedConfig ? (
    <div className="flex items-center justify-end px-4 py-2 border-b border-coval-border">
      <div className="flex items-center gap-2">
        <Switch
          id={versionToggleId}
          checked={includeOldVersions}
          onCheckedChange={setIncludeOldVersions}
        />
        <label
          htmlFor={versionToggleId}
          className="text-xs text-coval-text-muted cursor-pointer"
        >
          Include old versions
        </label>
      </div>
    </div>
  ) : null

  if (isLoading || (annotationProjectIds !== undefined && isFetching)) {
    return (
      <div>
        {toolbar}
        <div className="flex items-center justify-center py-12">
          <Skeleton className="h-4 w-16 rounded-md" />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div>
        {toolbar}
        <div className="flex items-center justify-center py-12 text-coval-danger-text text-sm">
          Failed to load annotations. Please try again.
        </div>
      </div>
    )
  }

  if (groupedData.length === 0) {
    return (
      <div>
        {toolbar}
        <div className="flex items-center justify-center py-12 text-coval-text-muted text-sm">
          No annotations found for this metric
        </div>
      </div>
    )
  }

  if (displayData.length === 0) {
    return (
      <div>
        {toolbar}
        <div className="flex items-center justify-center py-12 text-coval-text-muted text-sm">
          No results match the current filters
        </div>
      </div>
    )
  }

  return (
    <div>
      {toolbar}
      {focusedSimulationIds && (
        <div className="flex items-center gap-2 px-4 py-2 text-xs text-coval-info-on-surface bg-coval-info-surface border-b border-coval-border">
          <span>
            Showing {displayData.length} simulation
            {displayData.length === 1 ? "" : "s"} from matrix selection
          </span>
          <button
            type="button"
            className="text-coval-info-on-surface hover:underline"
            onClick={clearFocusedSimulationIds}
            aria-label="Clear matrix filter"
          >
            Clear
          </button>
        </div>
      )}
      {conflictIds.size > 0 && <DisagreementLegend />}
      {criteriaFirst ? (
        <ExpectedBehaviorComparisonList
          rows={displayData.map((data) => ({
            data,
            hasConflict: conflictIds.has(data.simulationOutputId),
          }))}
          canRunEvaluations={canRunEvaluations}
          expectedBehaviors={criteriaOverride ?? []}
          evaluatingIds={evaluatingIds}
          headerCheckedState={headerCheckedState}
          runIdSet={runIdSet}
          atCapacity={atCapacity}
          includeOldVersions={includeOldVersions}
          onToggleSelectAll={handleToggleSelectAll}
          onToggleRow={handleToggleRow}
          onOpenRow={handleRowClick}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          isActive={isActiveItem}
        />
      ) : (
        <table className="w-full text-left table-fixed border-separate border-spacing-0">
          <thead>
            <tr className="border-b border-coval-border">
              {canRunEvaluations && (
                <th className="sticky -top-px z-10 px-4 py-3 text-sm font-medium text-coval-text w-[44px] bg-surface-default border-b border-coval-border">
                  <Checkbox
                    checked={headerCheckedState}
                    onCheckedChange={handleToggleSelectAll}
                    aria-label="Select all conversations for testing"
                  />
                </th>
              )}
              <th className="sticky -top-px z-10 px-4 py-3 text-sm font-medium text-coval-text w-[100px] bg-surface-default border-b border-coval-border">
                Sim ID
              </th>
              <th className="sticky -top-px z-10 px-4 py-3 text-sm font-medium text-coval-text bg-surface-default border-b border-coval-border">
                AI Review
              </th>
              <th className="sticky -top-px z-10 px-4 py-3 text-sm font-medium text-coval-text bg-surface-default border-b border-coval-border">
                {showHumanAgreementRate ? "Human Agreement" : "Human Review"}
              </th>
              {canRunEvaluations && (
                <th className="sticky -top-px z-10 px-4 py-3 text-sm font-medium text-coval-text bg-surface-default border-b border-coval-border">
                  <div className="flex items-center justify-between">
                    <span>Most Recent Experiment</span>
                    {!isRunning && experimentAgreementStats && (
                      <span className="text-coval-warning-text flex items-center gap-0.5">
                        {experimentAgreementStats.percentage}% (
                        {experimentAgreementStats.agreements}/
                        {experimentAgreementStats.comparisons})
                      </span>
                    )}
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayData.map((data) => (
              <SimulationRow
                key={data.simulationOutputId}
                data={data}
                isEvaluating={evaluatingIds.has(data.simulationOutputId)}
                isExpanded={expandedRows.has(data.simulationOutputId)}
                isActive={isActiveItem(data.simulationOutputId)}
                isSelected={runIdSet.has(data.simulationOutputId)}
                disableSelect={
                  atCapacity && !runIdSet.has(data.simulationOutputId)
                }
                onToggleSelect={() => handleToggleRow(data.simulationOutputId)}
                onToggleExpand={() =>
                  handleToggleExpand(data.simulationOutputId)
                }
                onRowClick={handleRowClick}
                onPointerEnter={handlePointerEnter}
                onPointerLeave={handlePointerLeave}
                hasConflict={conflictIds.has(data.simulationOutputId)}
                onSetPrimary={(reviewId) => {
                  void handleSetPrimary(
                    reviewId,
                    data.humanReviews.map((r) => r.id),
                  )
                }}
                isSettingPrimary={data.humanReviews.some(
                  (r) => r.id === settingPrimaryId,
                )}
                canRunEvaluations={canRunEvaluations}
                metric={metric}
                criteriaViewMode={effectiveCriteriaViewMode}
                showHumanAgreementRate={showHumanAgreementRate}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
